import React, { useState } from "react";
import { ArrowDown, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";

export function userToComboboxOption(user) {
  const label =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.name || user.email;
  return {
    value: user.id || user._id,
    label,
    ...(user.email && label !== user.email ? { sublabel: user.email } : {}),
  };
}

export function Combobox({
  options = [],
  value,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  className = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const filteredOptions = options.filter((option) => {
    const query = searchValue.toLowerCase();
    return (
      option.label.toLowerCase().includes(query) ||
      (option.sublabel && option.sublabel.toLowerCase().includes(query))
    );
  });

  const selectedOption = options.find((option) => option.value === value);

  const getSelectedOptionClass = (option) => {
    if (disabled) {
      return "";
    }
    if (option.disabled) {
      return "text-gray-500 opacity-50";
    }
    if (option.invalid) {
      return "text-red-400 opacity-75";
    }
    return "";
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`flex h-52 w-full justify-between rounded-b-none rounded-t-lg bg-navy-100 p-4 text-sm font-normal text-gray-0 ${className}`}
          disabled={disabled}
        >
          {selectedOption ? (
            selectedOption.sublabel ? (
              <div
                className={`flex flex-col items-start text-left ${getSelectedOptionClass(selectedOption)}`}
              >
                <span>{selectedOption.label}</span>
                <span className="text-xs text-gray-400">{selectedOption.sublabel}</span>
              </div>
            ) : (
              <span className={getSelectedOptionClass(selectedOption)}>
                {selectedOption.label}
              </span>
            )
          ) : (
            placeholder
          )}
          <ArrowDown className="w-4 h-4 ml-2 text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-50 mt-1 w-[--radix-popover-trigger-width] overflow-hidden border border-none bg-navy-100 text-white shadow-md">
        <div className="5 rounded-b-none rounded-t-md border-[0.5px] bg-navy-100 py-1 pr-3">
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="h-8 rounded-lg"
          />
        </div>
        <div className="scrollbar-hide max-h-60 overflow-y-auto [&::-webkit-scrollbar]:hidden">
          {filteredOptions.length === 0 ? (
            <div className="p-2 text-sm text-gray-500">No options found.</div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`flex cursor-pointer items-center px-2 py-1.5 text-sm ${option.sublabel ? "border-b border-gray-600 last:border-b-0" : ""
                  } ${option.disabled
                    ? "cursor-not-allowed text-gray-500 opacity-50"
                    : option.invalid
                      ? "cursor-not-allowed text-red-400 opacity-75"
                      : value === option.value
                        ? "text-white hover:text-white"
                        : "text-stone-300 hover:text-white"
                  }`}
                onClick={() => {
                  if (option.disabled) return; // Prevent selection of disabled options
                  onValueChange(option.value);
                  setOpen(false);
                  setSearchValue("");
                }}
              >
                <Check
                  className={`mr-2 h-4 w-4 shrink-0 self-start mt-0.5 ${value === option.value ? "opacity-100" : "opacity-0"}`}
                />
                {option.sublabel ? (
                  <div className="flex flex-col py-0.5">
                    <span>{option.label}</span>
                    <span className="text-xs text-gray-400">{option.sublabel}</span>
                  </div>
                ) : (
                  option.label
                )}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
